<template>
    <!-- The Forge inside a rock, for its milestones only (Keelan, 21 Oct): drafts the steps the rock still needs from
         its name, Done statement and the milestones it has, dated between today and the quarter's end. The draft is
         checked and edited here, then added to the rock's form; the rock's Save keeps them (rocks.ts). -->
    <div class="frm" :class="{ 'frm-busy': busy }">
        <div class="frm-head">
            <div class="frm-icon"><svg-icon icon="twousers" /></div>
            <div class="frm-head-text">
                <div class="frm-title">The Forge · milestones</div>
                <div class="frm-sub">Drafts the steps this rock still needs, dated through the quarter. Its milestones stay; nothing is saved until you save the rock.</div>
            </div>
        </div>

        <template v-if="phase == 'input'">
            <textarea v-model="direction" rows="2" maxlength="2000" :disabled="busy" class="frm-text" placeholder="Anything the Forge should know? e.g. the first step is already under way, or keep it to three steps"></textarea>
            <div class="frm-row">
                <div class="btn frm-btn" :class="canDraft ? '' : 'disable-button'" @click="draft(false)">{{ busy ? 'Drafting…' : (form.Milestones.length ? 'Draft the missing milestones' : 'Draft milestones') }}</div>
                <span v-if="!nameOk" class="frm-hint">Name the rock first.</span>
                <span v-else-if="added" class="frm-hint">Added {{ added }}. Save the rock to keep {{ added == 1 ? 'it' : 'them' }}.</span>
            </div>
        </template>

        <template v-else>
            <p v-if="summary" class="frm-summary">{{ summary }}</p>
            <div v-for="(r, i) in rows" :key="i" class="frm-ms" :class="r.keep ? '' : 'frm-ms-off'">
                <input type="checkbox" v-model="r.keep" aria-label="Add this milestone" />
                <input type="text" v-model="r.text" class="frm-ms-text" aria-label="Milestone" />
                <select v-model="r.ownerEmployeeID" class="frm-ms-own" aria-label="Milestone owner">
                    <option :value="null">{{ form.OwnerEmployeeID ? 'Rock owner' : 'No owner' }}</option>
                    <option v-for="e in employees" :key="e.ID" :value="e.ID">{{ e.Name }}</option>
                </select>
                <input type="date" v-model="r.dueDate" :min="earliest" :max="bounds.end" class="frm-ms-date" aria-label="Due date" />
            </div>
            <div v-if="!rows.length" class="frm-hint frm-none">Nothing new suggested; it already looks planned. Say what you want below and draft again.</div>
            <textarea v-model="direction" rows="2" maxlength="2000" :disabled="busy" class="frm-text" placeholder="Want anything changed? e.g. shorter steps, or finish by mid-November"></textarea>
            <div class="frm-row">
                <div class="btn frm-btn" :class="keptCount && !busy ? '' : 'disable-button'" @click="add()">Add {{ keptCount }} milestone{{ keptCount == 1 ? '' : 's' }}</div>
                <div class="btn-light frm-btn" :class="busy ? 'disable-button' : ''" @click="draft(true)">{{ busy ? 'Drafting…' : 'Draft again' }}</div>
                <a class="frm-link" @click.prevent="reset()">Cancel</a>
            </div>
        </template>

        <div v-if="error" class="frm-error" role="alert">{{ error }}</div>
    </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import axios from 'axios';
import SvgIcon from './SvgIcon.vue';
import { quarterBounds } from '@/rocks';
import { milestoneDraftContext, draftedMilestones, earliestDue, validDate } from '@/rockforge';

export default defineComponent({
    name: 'ForgeRockMilestones',
    // rockID: the saved rock's ID, 0 for a new one. form: RockModal's form (read here, never changed). quarter: "YYYY-Qn".
    props: [ 'rockID', 'form', 'quarter', 'employees' ],
    emits: [ 'add' ],
    components: { SvgIcon },
    data() {
        return {
            phase: 'input' as 'input' | 'review',
            busy: false,
            direction: '',
            description: '', // what the first draft was asked for; Draft again keeps it and sends direction as feedback
            previous: null as any,
            summary: '',
            rows: [] as any[],
            error: '',
            added: 0
        }
    },
    computed: {
        bounds(): { start: string, end: string } { return quarterBounds(this.quarter); },
        earliest(): string { return earliestDue(this.quarter); },
        nameOk(): boolean { return !!String(this.form.Name || '').trim(); },
        canDraft(): boolean { return this.nameOk && !this.busy; },
        keptCount(): number { return this.rows.filter((r: any) => r.keep && String(r.text || '').trim() && validDate(r.dueDate)).length; }
    },
    methods: {
        // revise: Draft again from the review, with what was typed as feedback on the previous draft
        async draft(revise: boolean) {
            if(this.busy || !this.nameOk) return;
            this.busy = true;
            this.error = '';
            this.added = 0;
            const note = this.direction.trim();
            if(!revise) this.description = note || 'Plan the milestones for this rock.';
            try {
                const resp = await axios.post(`${this.$store.state.apiUrl}/api/ai/team-draft`, {
                    kind: 'milestones', description: this.description,
                    context: milestoneDraftContext(Number(this.rockID) || 0, this.form, this.quarter, this.employees || []),
                    previous: revise ? this.previous : null, feedback: revise && note ? note : null
                });
                this.previous = resp.data;
                this.summary = String(resp.data?.summary || '');
                this.rows = draftedMilestones(resp.data, Number(this.rockID) || 0, this.form, this.quarter, this.employees || []);
                this.direction = '';
                this.phase = 'review';
            } catch(e: any) {
                this.error = this.errorText(e);
            }
            this.busy = false;
        },
        // Onto the rock's form, in the modal's milestone shape (null owner = the rock's owner, as its own rows do)
        add() {
            if(this.busy || !this.keptCount) return;
            const kept = this.rows.filter((r: any) => r.keep && String(r.text || '').trim() && validDate(r.dueDate));
            this.$emit('add', kept.map((r: any) => ({ Text: String(r.text).trim(), OwnerEmployeeID: r.ownerEmployeeID ?? (this.form.OwnerEmployeeID || null), DueDate: r.dueDate, Done: false })));
            this.added = kept.length;
            this.reset();
        },
        reset() {
            this.phase = 'input';
            this.rows = [];
            this.summary = '';
            this.previous = null;
            this.direction = '';
            this.error = '';
        },
        errorText(e: any): string {
            const d = e?.response?.data;
            return typeof d == 'string' && d.trim() && d.length < 300 ? d : 'The Forge couldn\'t draft that. Please try again.';
        }
    }
});
</script>

<style scoped>
.frm { margin-top: 14px; padding: 12px 14px 12px; border: 1px solid var(--mid-gray); border-radius: 10px; background: #fbf8f4; }
.frm-head { display: flex; gap: 10px; align-items: flex-start; margin-bottom: 8px; }
.frm-icon { width: 30px; height: 30px; border-radius: 8px; background: var(--primary-color); display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.frm-icon svg { width: 16px; height: 16px; fill: #fff; }
.frm-head-text { min-width: 0; }
.frm-title { font-size: 14px; font-weight: bold; line-height: 1.3; }
.frm-sub { font-size: 12px; color: var(--font-mid-gray); line-height: 1.4; }
.frm textarea.frm-text { margin: 0 0 8px !important; min-height: 44px; resize: vertical; font-family: inherit; font-size: 13px !important; padding: 7px 9px !important; }
.frm-row { display: flex; align-items: center; flex-wrap: wrap; gap: 8px 12px; }
.frm-row .btn, .frm-row .btn-light { margin: 0; padding: 7px 14px; font-size: 13px; }
.frm-hint { font-size: 12px; color: var(--font-mid-gray); }
.frm-none { margin-bottom: 8px; }
.frm-summary { margin: 0 0 8px; font-size: 13px; line-height: 1.45; }
.frm-ms { display: flex; align-items: center; gap: 6px; margin-bottom: 6px; }
.frm-ms input, .frm-ms select { margin: 0 !important; padding: 7px 9px !important; font-size: 13px !important; }
.frm-ms input[type=checkbox] { width: 16px; height: 16px; flex-shrink: 0; padding: 0 !important; accent-color: var(--primary-color); }
.frm-ms-text { flex: 1; min-width: 0; }
.frm-ms-off .frm-ms-text, .frm-ms-off .frm-ms-own, .frm-ms-off .frm-ms-date { opacity: .5; }
.frm-ms-own { width: 130px; flex-shrink: 0; }
.frm-ms-date { width: 140px; flex-shrink: 0; }
.frm-link { color: var(--font-dark-gray); font-weight: 600; text-decoration: underline; cursor: pointer; font-size: 13px; }
.frm-link:hover { color: var(--primary-color); }
.frm-error { margin-top: 8px; color: var(--primary-color); font-size: 13px; }
.frm-busy { opacity: .85; }

@media(max-width: 767px) {
    .frm-ms { flex-wrap: wrap; }
    .frm-ms-text { flex-basis: calc(100% - 30px); }
}
</style>
